import React, { useState } from 'react'
import { ActivityLogViewer } from './index'
import { formatDate } from '@edge-stream/utils'
import { BarChartIcon, CloseIcon } from '@bizfirst/common-react'

interface FloatingActivityStreamPanelProps {
  isOpen: boolean
  onClose: () => void
  messages?: any[]
}

export function FloatingActivityStreamPanel({ isOpen, onClose, messages = [] }: FloatingActivityStreamPanelProps) {
  const [isResizing, setIsResizing] = useState(false)
  const [width, setWidth] = useState(400)

  const activityLog = messages.map((msg) => {
    const time = formatDate((msg.meta?.receivedAt || new Date()).toISOString())
    return `[${time}] ${msg.meta?.topic || 'unknown'}`
  })


  const handleMouseDown = () => {
    setIsResizing(true)
  }

  React.useEffect(() => {
    const handleMouseUp = () => setIsResizing(false)
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return
      const newWidth = Math.max(300, window.innerWidth - e.clientX)
      setWidth(newWidth)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isResizing])

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        bottom: 0,
        width: `${width}px`,
        height: '50vh',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-4px -4px 16px rgba(0, 0, 0, 0.2)',
        zIndex: 1000,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-tertiary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'grab',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <BarChartIcon /> Activity Stream
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
          }}
          title="Close activity stream"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          padding: '12px',
          gap: '12px',
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'var(--text-secondary)',
            }}
          >
            <p>No messages yet</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <ActivityLogViewer activities={activityLog.slice(-50)} />
          </div>
        )}
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '4px',
          height: '100%',
          cursor: 'col-resize',
          background: isResizing ? 'var(--primary)' : 'transparent',
          opacity: isResizing ? 0.5 : 0,
          transition: 'opacity 0.2s',
        }}
      />
    </div>
  )
}
